import { useCallback, useState } from "react";

import type { Tag } from "../types/types";
import { useTagCrud } from "./useTagCrud";
import { useTagMutations } from "./useTagMutations";

import { validateTagName } from "@/utils/tagFiltering";

/**
 * Props for useTagCreation hook
 */
interface UseTagCreationProps {
  /**
   * ID of the player to add created tags to
   */
  playerId: string;
  /**
   * ID of the Kanban board for context-specific tag operations
   */
  boardId: string;
  /**
   * Callback invoked when tags are successfully changed for the player
   */
  onTagsChanged?: (tagName: string) => void;
  /**
   * Callback invoked immediately for optimistic UI updates when a tag is being created
   */
  onOptimisticTagAdd: (tag: Tag) => void;
}

/**
 * Hook that handles tag creation logic with validation and optimistic updates using Apollo Client
 * @remarks
 * This hook uses Apollo-based mutations for tag creation via useTagCrud hook.
 * It provides instant optimistic UI updates before the server responds.
 * @param props - The hook props
 * @param props.playerId - ID of the player to add newly created tags to
 * @param props.boardId - ID of the Kanban board for context-specific tag operations and analytics
 * @param props.onTagsChanged - Callback invoked after a tag is successfully created and added, receives the new tag name
 * @param props.onOptimisticTagAdd - Callback invoked immediately with optimistic tag data for instant UI feedback before server response
 * @returns Object with isCreating state and handlers for creating tags with optimistic updates
 */
function useTagCreation({
  playerId,
  boardId,
  onTagsChanged,
  onOptimisticTagAdd,
}: UseTagCreationProps) {
  const { createTag } = useTagCrud(boardId);
  const { addTagsToPlayer } = useTagMutations(boardId);

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateAndAddTag = useCallback(
    async (tagName: string) => {
      if (!tagName.trim() || isCreating) return;

      // Validate tag name before creation
      const validation = validateTagName(tagName.trim());
      if (!validation.isValid) {
        console.error("Invalid tag name:", validation.error);
        return;
      }

      setIsCreating(true);
      // Use a more collision-resistant ID generation for temporary IDs
      // This is not for security but for uniqueness - no crypto needed
      const uniqueSuffix =
        Date.now().toString(36) +
        performance.now().toString(36).replace(".", "");
      const tempTagId = `temp-${uniqueSuffix}`;

      // INSTANT optimistic UI - show new tag immediately
      const optimisticTag: Tag = {
        id: tempTagId,
        name: tagName.trim(),
        cardCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        color: "#3B82F6", // Default blue color (primary)
      };

      onOptimisticTagAdd(optimisticTag);
      console.log(
        `⚡ [INSTANT CREATE] Added optimistic tag "${optimisticTag.name}"`
      );

      try {
        const newTag = await createTag(tagName.trim());
        await addTagsToPlayer(playerId, [newTag.id]);

        onTagsChanged?.(newTag.name);
        console.log(
          `✅ [INSTANT CREATE] Created and added tag "${newTag.name}"`
        );

        return { success: true, tag: newTag, tempTagId };
      } catch (error) {
        console.error("Failed to create tag:", error);
        return { success: false, tempTagId };
      } finally {
        setIsCreating(false);
      }
    },
    [
      isCreating,
      createTag,
      addTagsToPlayer,
      playerId,
      onTagsChanged,
      onOptimisticTagAdd,
    ]
  );

  const handleCreateFromSearch = useCallback(
    async (searchQuery: string) => {
      return handleCreateAndAddTag(searchQuery);
    },
    [handleCreateAndAddTag]
  );

  return {
    // State
    isCreating,

    // Handlers
    handleCreateAndAddTag,
    handleCreateFromSearch,
  };
}

export default useTagCreation;
